import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { useAuth } from "@/auth/auth";
import { Btn, Panel } from "@/design/primitives";
import { Logo } from "@/shell/BootScreen";

/**
 * Souhlas s připojením MCP konektoru (OAuth authorize). Server sem přesměruje
 * z `/oauth/authorize` poté, co ověří klienta a redirect_uri; tahle obrazovka
 * ukazuje, kdo o přístup žádá, a odpověď posílá zpátky serveru, který teprve
 * vydá kód.
 *
 * Záměrně nejde jen o „OK“: token dostane práva přihlášeného uživatele, takže
 * musí být vidět, čí jménem konektor pojede a kam se bude vracet.
 */
export function OAuthConsentPage() {
  const { session, loading } = useAuth();
  const requestId = new URLSearchParams(window.location.search).get("request") ?? "";
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["oauth-consent", requestId],
    queryFn: () => api.oauthConsent(requestId),
    enabled: Boolean(requestId) && Boolean(session),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (approve: boolean) => api.oauthConsentDecide(requestId, approve),
    // odchod z SPA zpět ke klientovi (localhost callback Claude Desktopu)
    onSuccess: (res) => window.location.replace(res.redirectTo),
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : cs.common.error),
  });

  if (loading) return null;
  if (!session) {
    // server sem posílá i nepřihlášené (přímý zásah do URL) — zpět na login
    // s návratem, ať uživatel neztratí rozdělaný flow
    const next = `/oauth/consent?request=${encodeURIComponent(requestId)}`;
    window.location.replace(`/login?next=${encodeURIComponent(next)}`);
    return null;
  }

  const detail = query.data;
  const failed = query.error instanceof ApiRequestError ? query.error.message : null;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Panel className="w-full max-w-[440px] p-6">
        <div className="mb-5 flex items-center gap-3">
          <Logo size={26} />
          <div className="af-label" style={{ color: "var(--accent)" }}>
            {cs.oauth.title}
          </div>
        </div>

        {failed ? (
          <div className="text-[11px]" style={{ color: "var(--critical)" }}>
            ⚠ {failed}
          </div>
        ) : !detail ? null : (
          <>
            <p className="text-[12px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
              {cs.oauth.intro(detail.clientName)}
            </p>
            <div
              className="mt-4 flex flex-col gap-1.5 p-3 text-[12px]"
              style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-subtle)", background: "var(--surface-inset)" }}
            >
              <Row label={cs.oauth.asUser} value={`${detail.user.name} (${detail.user.role})`} />
              <Row label={cs.oauth.returnsTo} value={detail.redirectHost} />
              <Row label={cs.oauth.access} value={cs.oauth.accessValue} />
            </div>
            <p className="mt-3 text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {cs.oauth.note}
            </p>

            {error ? (
              <div className="mt-3 text-[12px]" style={{ color: "var(--critical)" }}>
                ⚠ {error}
              </div>
            ) : null}

            <div className="mt-5 flex items-center justify-end gap-3">
              <Btn disabled={decide.isPending} onClick={() => decide.mutate(false)}>
                {cs.oauth.deny}
              </Btn>
              <Btn
                variant="primary"
                disabled={decide.isPending}
                onClick={() => decide.mutate(true)}
                data-testid="oauth-approve"
              >
                {cs.oauth.approve}
              </Btn>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="af-label-dim">{label}</span>
      <span className="truncate" style={{ color: "var(--text-primary)" }}>
        {value}
      </span>
    </div>
  );
}
